import React from 'react';
import type { Position } from '@axiom/validation';
import {
  Well,
  CondensedLarge,
  Flashy,
  Gutter,
  Layout,
  LayoutItem,
  useApi,
  DateUtil,
  PluralizeUtil,
} from '@axiom/ui';

import { CandidateOpportunitiesLegacyApi } from '../../api/candidate-opportunities-legacy-api';
import { useCandidateId } from '../../hooks/useCandidateId';
import { EngagementsUtil } from '../../utils/engagements-util';

export type PreferredWeeklyHoursEngagementsProps = {
  name?: string;
};

export const PreferredWeeklyHoursEngagements = ({
  name = 'PREFERRED_WEEKLY_HOURS_ENGAGEMENTS',
}: PreferredWeeklyHoursEngagementsProps = {}) => {
  const candidateId = useCandidateId();
  const [{ data: opportunities }] = useApi(
    CandidateOpportunitiesLegacyApi.readOpportunitiesByCandidateId(candidateId)
  );

  const getHoursString = (
    billingHoursPerWeek: Position['billingHoursPerWeek']
  ) => {
    return billingHoursPerWeek === null
      ? 'Unknown hours'
      : `${billingHoursPerWeek} ${PluralizeUtil({ text: 'hour', count: billingHoursPerWeek })}`;
  };

  const currentEngagements = EngagementsUtil.filterCurrentEngagements({
    opportunities,
  }).map(e => {
    return {
      id: e.id,
      jobName: e.jobName,
      hours: getHoursString(e.position?.billingHoursPerWeek ?? null),
      // hours: `${e.position?.billingHoursPerWeek ?? 'Unknown'} hours`,
      suffix:
        e.position?.endDateStatus === 'Confirmed'
          ? '(end date confirmed)'
          : null,
    };
  });

  const futureEngagements = EngagementsUtil.filterFutureEngagements({
    opportunities,
  }).map(e => {
    return {
      id: e.id,
      jobName: e.jobName,
      hours: getHoursString(e.position?.billingHoursPerWeek ?? null),
      suffix: `(start date: ${DateUtil.displayLongMonthDayYear(e.position?.startDate) ?? 'pending'})`,
    };
  });

  return (
    <div data-test={name}>
      {currentEngagements.length > 0 ? (
        <Gutter bottom="16px">
          <Well>
            <Gutter bottom="8px">
              <Flashy bold>
                <CondensedLarge>Current Engagements</CondensedLarge>
              </Flashy>
            </Gutter>
            <Layout
              verticalGutter="8px"
              direction="vertical"
              name="CURRENT_ENGAGEMENTS"
            >
              {currentEngagements.map(e => (
                <LayoutItem key={e.id} name="CURRENT_ENGAGEMENT">
                  <CondensedLarge name={e.id}>
                    {e.jobName}: {e.hours} {e.suffix}
                  </CondensedLarge>
                </LayoutItem>
              ))}
            </Layout>
          </Well>
        </Gutter>
      ) : null}
      {futureEngagements.length > 0 ? (
        <Gutter bottom="16px">
          <Well>
            <Gutter bottom="8px">
              <Flashy bold>
                <CondensedLarge>Future Engagements</CondensedLarge>
              </Flashy>
            </Gutter>
            <Layout
              verticalGutter="8px"
              direction="vertical"
              name="FUTURE_ENGAGEMENTS"
            >
              {futureEngagements.map(e => (
                <LayoutItem key={e.id} name="FUTURE_ENGAGEMENT">
                  <CondensedLarge name={e.id}>
                    {e.jobName}: {e.hours} {e.suffix}
                  </CondensedLarge>
                </LayoutItem>
              ))}
            </Layout>
          </Well>
        </Gutter>
      ) : null}
    </div>
  );
};
